import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Icon } from '../components/Icon';
import { mutationErrorMessage } from '../lib/gqlError';
import { RefreshButton } from '../components/RefreshButton';

const JOBS = gql`
  query JobDefinitions {
    jobDefinitions {
      name
      handlerKey
      description
      scheduleCron
      enabled
      singleton
      params
      updatedBy
      lastRun {
        id
        status
        startedAt
        finishedAt
        triggeredBy
        error
      }
    }
  }
`;
const RUNS = gql`
  query JobRuns($jobName: String!) {
    jobRuns(jobName: $jobName, limit: 20) {
      id
      status
      startedAt
      finishedAt
      triggeredBy
      error
      stats
    }
  }
`;
const SET_ENABLED = gql`
  mutation SetEnabled($name: String!, $enabled: Boolean!) {
    setJobEnabled(name: $name, enabled: $enabled) {
      name
      enabled
    }
  }
`;
const RUN_NOW = gql`
  mutation RunNow($name: String!) {
    runJobNow(name: $name) {
      name
    }
  }
`;

interface Run {
  id: string;
  status: string;
  startedAt: string;
  finishedAt?: string;
  triggeredBy: string;
  error?: string;
  stats?: string;
}
interface JobDef {
  name: string;
  handlerKey: string;
  description?: string;
  scheduleCron?: string;
  enabled: boolean;
  singleton: boolean;
  params: string;
  updatedBy?: string;
  lastRun?: Run;
}
interface Data {
  jobDefinitions: JobDef[];
}

const STATUS_CLASS: Record<string, string> = {
  succeeded: 'confirmed',
  running: '',
  failed: 'inferred',
  skipped: 'inferred',
};

export function SchedulerPage() {
  // Poll so a run-now / status change shows up without a manual refresh.
  const { data, loading, error, refetch } = useQuery<Data>(JOBS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 4000,
  });
  // Which job the last write targeted, so a rejection is rendered against THAT row
  // and not against all of them. A ref, not state: it is set on the click that also
  // fires the mutation, and the mutation's own state change is what re-renders.
  const lastToggled = useRef('');
  const lastRunNow = useRef('');
  // `onError` is what makes the tuple's `error` reachable instead of an unhandled
  // rejection: without it a Forbidden/expired-jwt refusal resolved to nothing at all
  // and the click looked like a no-op (e-39). The message itself is read from the
  // tuple below — this handler exists to own the rejection, not to duplicate it.
  const [setEnabled, { error: toggleError }] = useMutation(SET_ENABLED, {
    onCompleted: () => refetch(),
    onError: () => {},
  });
  const [runNow, { loading: running, error: runError }] = useMutation(RUN_NOW, {
    onCompleted: () => refetch(),
    onError: () => {},
  });
  const [historyOf, setHistoryOf] = useState<string | null>(null);
  const toggleErrMsg = mutationErrorMessage(toggleError);
  const runErrMsg = mutationErrorMessage(runError);

  if (loading && !data) return <div className="page">Loading jobs…</div>;
  if (error)
    return (
      <div className="page">
        <p className="muted">Failed to load: {error.message}</p>
        <p className="muted">Is the API up? Scheduler tables live in the same DB.</p>
      </div>
    );

  const jobs = data?.jobDefinitions ?? [];
  const enabledCount = jobs.filter((j) => j.enabled).length;

  return (
    <div className="page">
      <div className="stat-row">
        <Stat label="Jobs" value={jobs.length} />
        <Stat label="Enabled" value={`${enabledCount} / ${jobs.length}`} />
      </div>

      <div className="filter-row">
        <RefreshButton refetch={() => refetch().catch(() => undefined)} />
      </div>

      {jobs.length === 0 ? (
        <p className="muted">
          No jobs registered. Start the scheduler worker so it seeds job_definition from code.
        </p>
      ) : (
        <table className="data-list--primary">
          <thead>
            <tr>
              <th>Job</th>
              <th>Handler</th>
              <th>Schedule</th>
              <th>Enabled</th>
              <th>Last run</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.name}>
                <td>
                  <Link className="table-title" to={`/scheduler/${encodeURIComponent(j.name)}`}>
                    {j.name}
                  </Link>
                  {/* Hover (or keyboard-focus) reveals the full text — the list stays one
                      line per job, and nothing is truncated. `tabIndex` + `role="note"` so
                      it is reachable without a mouse; the full text is also on the job page. */}
                  {j.description && (
                    <span className="info-tip" tabIndex={0} role="note" aria-label={j.description}>
                      <span className="info-tip__mark" aria-hidden="true">
                        !
                      </span>
                      <span className="info-tip__body">{j.description}</span>
                    </span>
                  )}
                </td>
                <td className="muted">{j.handlerKey}</td>
                <td className="muted">
                  <code>{j.scheduleCron ?? '—'}</code>
                </td>
                <td>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={j.enabled}
                      onChange={(e) => {
                        lastToggled.current = j.name;
                        setEnabled({ variables: { name: j.name, enabled: e.target.checked } });
                      }}
                    />
                    <span>{j.enabled ? 'on' : 'off'}</span>
                  </label>
                  {/* The checkbox is driven by `j.enabled` from the query, never by a
                      local optimistic flag, so a refused write leaves it showing the
                      server's value — the revert is the absence of a lie, not a undo. */}
                  {toggleErrMsg && lastToggled.current === j.name && (
                    <div className="run-error" role="alert" style={{ marginTop: 4, maxWidth: 280 }}>
                      {toggleErrMsg}
                    </div>
                  )}
                </td>
                <td className="muted">
                  {j.lastRun ? new Date(j.lastRun.startedAt).toLocaleString() : '—'}
                  {j.lastRun && (
                    <span className="text-2xs" style={{ display: 'block' }}>via {j.lastRun.triggeredBy}</span>
                  )}
                </td>
                <td>
                  {j.lastRun ? (
                    <span className={`badge ${STATUS_CLASS[j.lastRun.status] ?? ''}`}>{j.lastRun.status}</span>
                  ) : (
                    <span className="muted">never run</span>
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button
                    className="mini-btn"
                    disabled={running}
                    onClick={() => {
                      lastRunNow.current = j.name;
                      runNow({ variables: { name: j.name } });
                    }}
                  >
                    <Icon name="run" /> Run now
                  </button>
                  <button
                    className="mini-btn"
                    onClick={() => setHistoryOf(historyOf === j.name ? null : j.name)}
                  >
                    History
                  </button>
                  {runErrMsg && lastRunNow.current === j.name && (
                    <div
                      className="run-error"
                      role="alert"
                      style={{ marginTop: 4, maxWidth: 280, whiteSpace: 'normal' }}
                    >
                      {runErrMsg}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {historyOf && <HistoryPanel jobName={historyOf} onClose={() => setHistoryOf(null)} />}
    </div>
  );
}

function HistoryPanel({ jobName, onClose }: { jobName: string; onClose: () => void }) {
  const { data, loading, refetch } = useQuery<{ jobRuns: Run[] }>(RUNS, {
    variables: { jobName },
    fetchPolicy: 'cache-and-network',
    pollInterval: 4000,
  });
  const runs = data?.jobRuns ?? [];
  return (
    <div className="history-panel">
      <div className="history-head">
        <strong>Run history — {jobName}</strong>
        <RefreshButton refetch={() => refetch().catch(() => undefined)} />
        <button className="mini-btn" onClick={onClose}>
          close
        </button>
      </div>
      {loading && !data ? (
        <p className="muted">Loading…</p>
      ) : runs.length === 0 ? (
        <p className="muted">No runs yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Started</th>
              <th>Status</th>
              <th>Trigger</th>
              <th>Duration</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id}>
                <td className="muted">{new Date(r.startedAt).toLocaleString()}</td>
                <td>
                  <span className={`badge ${STATUS_CLASS[r.status] ?? ''}`}>{r.status}</span>
                </td>
                <td className="muted">{r.triggeredBy}</td>
                <td className="muted">{duration(r.startedAt, r.finishedAt)}</td>
                <td className="muted text-2xs" style={{ maxWidth: 360 }}>
                  {r.error ? r.error : summarize(r.stats)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}
function duration(start: string, end?: string): string {
  if (!end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}
function summarize(statsJson?: string): string {
  if (!statsJson) return '';
  try {
    const s = JSON.parse(statsJson);
    const repos = s?.stats?.repos ?? s?.repos;
    if (repos) return `repos: +${repos.added ?? 0} / ${repos.total ?? '?'} (${repos.unchanged ?? 0} unchanged)`;
    return statsJson.slice(0, 120);
  } catch {
    return '';
  }
}
